import React, { useEffect, useState } from "react";
import CcButton from "../../CoilConfigurator/components/CcButton";
import InputWithUnitSelect from "../../CoilConfigurator/components/InputWithUnitSelect";
import {
  emptyPipeSpecification,
  emptyProcessSpecification,
  emptyProjectInformation,
  type ConfigurationResult,
  type GeometryUnit,
  type PipeSpecification,
  type ProcessSpecification,
  type ProjectInformation,
  type ProjectSpecification,
  type ThicknessUnit,
} from "../models/types";

const GEOMETRY_UNITS: Array<{ value: GeometryUnit; label: string }> = [
  { value: "mm", label: "mm" },
  { value: "in", label: "in" },
];

const THICKNESS_UNITS: Array<{ value: ThicknessUnit; label: string }> = [
  { value: "mm", label: "mm" },
  { value: "in", label: "in" },
  { value: "mil", label: "mil" },
];

interface EditConfigurationModalProps {
  open: boolean;
  result: ConfigurationResult | null;
  saving?: boolean;
  error?: string | null;
  onClose: () => void;
  onSave: (spec: ProjectSpecification) => void;
}

const Field = ({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) => (
  <div className="modal-field">
    <label htmlFor={id}>{label}</label>
    {children}
  </div>
);

const resultToSpec = (result: ConfigurationResult): ProjectSpecification => ({
  project: {
    ...emptyProjectInformation(),
    ...result.project,
  },
  pipe: {
    ...emptyPipeSpecification(),
    ...result.pipe,
  },
  process: {
    ...emptyProcessSpecification(),
    ...result.process,
  },
});

const EditConfigurationModal = ({
  open,
  result,
  saving = false,
  error = null,
  onClose,
  onSave,
}: EditConfigurationModalProps) => {
  const [project, setProject] = useState<ProjectInformation>(emptyProjectInformation());
  const [pipe, setPipe] = useState<PipeSpecification>(emptyPipeSpecification());
  const [process, setProcess] = useState<ProcessSpecification>(emptyProcessSpecification());

  useEffect(() => {
    if (!open || !result) return;
    const spec = resultToSpec(result);
    setProject(spec.project);
    setPipe(spec.pipe);
    setProcess(spec.process);
  }, [open, result]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !saving) onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, saving]);

  if (!open || !result) return null;

  const patchProject = (patch: Partial<ProjectInformation>) =>
    setProject((prev) => ({ ...prev, ...patch }));
  const patchPipe = (patch: Partial<PipeSpecification>) =>
    setPipe((prev) => ({ ...prev, ...patch }));
  const patchProcess = (patch: Partial<ProcessSpecification>) =>
    setProcess((prev) => ({ ...prev, ...patch }));

  return (
    <div
      className="dialog-scrim"
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget && !saving) onClose();
      }}
    >
      <div
        className="dialog-card dialog-card--wide op-edit-config-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="op-edit-config-title"
      >
        <div className="dialog-header">
          <h2 className="dialog-title" id="op-edit-config-title">
            Edit Configuration
          </h2>
          <CcButton variant="icon" aria-label="Close" onClick={onClose} disabled={saving}>
            ✕
          </CcButton>
        </div>

        <div className="dialog-body op-edit-config-modal__body">
          <section className="op-edit-section">
            <h3 className="op-edit-section__title">Project Information</h3>
            <div className="op-edit-grid">
              <Field id="edit-projectNumber" label="Project Number">
                <input
                  id="edit-projectNumber"
                  className="op-input"
                  value={project.projectNumber}
                  onChange={(event) => patchProject({ projectNumber: event.target.value })}
                />
              </Field>
              <Field id="edit-projectName" label="Project Name">
                <input
                  id="edit-projectName"
                  className="op-input"
                  value={project.projectName}
                  onChange={(event) => patchProject({ projectName: event.target.value })}
                />
              </Field>
              <Field id="edit-customer" label="Customer">
                <input
                  id="edit-customer"
                  className="op-input"
                  value={project.customer}
                  onChange={(event) => patchProject({ customer: event.target.value })}
                />
              </Field>
              <Field id="edit-engineer" label="Engineer">
                <input
                  id="edit-engineer"
                  className="op-input"
                  value={project.engineer}
                  onChange={(event) => patchProject({ engineer: event.target.value })}
                />
              </Field>
              <Field id="edit-projectRevision" label="Project Revision">
                <input
                  id="edit-projectRevision"
                  className="op-input"
                  value={project.projectRevision}
                  onChange={(event) => patchProject({ projectRevision: event.target.value })}
                />
              </Field>
              <Field id="edit-configurationDate" label="Configuration Date">
                <input
                  id="edit-configurationDate"
                  type="date"
                  className="op-input"
                  value={project.configurationDate}
                  onChange={(event) => patchProject({ configurationDate: event.target.value })}
                />
              </Field>
            </div>
          </section>

          <section className="op-edit-section">
            <h3 className="op-edit-section__title">Pipe Specification</h3>
            <div className="op-edit-grid">
              <Field id="edit-outsideDiameter" label="Pipe Outside Diameter">
                <InputWithUnitSelect
                  id="edit-outsideDiameter"
                  name="outsideDiameter"
                  value={pipe.outsideDiameter}
                  unit={pipe.outsideDiameterUnit}
                  unitOptions={GEOMETRY_UNITS}
                  onValueChange={(value) => patchPipe({ outsideDiameter: value })}
                  onUnitChange={(unit) => patchPipe({ outsideDiameterUnit: unit })}
                />
              </Field>
              <Field id="edit-wallThickness" label="Pipe Wall Thickness">
                <InputWithUnitSelect
                  id="edit-wallThickness"
                  name="wallThickness"
                  value={pipe.wallThickness}
                  unit={pipe.wallThicknessUnit}
                  unitOptions={THICKNESS_UNITS}
                  onValueChange={(value) => patchPipe({ wallThickness: value })}
                  onUnitChange={(unit) => patchPipe({ wallThicknessUnit: unit })}
                />
              </Field>
              <Field id="edit-pipeGrade" label="Pipe Grade">
                <input
                  id="edit-pipeGrade"
                  className="op-input"
                  placeholder="For example X65"
                  value={pipe.pipeGrade}
                  onChange={(event) => patchPipe({ pipeGrade: event.target.value })}
                />
              </Field>
              <Field id="edit-pipeLength" label="Pipe Length">
                <InputWithUnitSelect
                  id="edit-pipeLength"
                  name="pipeLength"
                  value={pipe.pipeLength}
                  unit={pipe.pipeLengthUnit}
                  unitOptions={GEOMETRY_UNITS}
                  onValueChange={(value) => patchPipe({ pipeLength: value })}
                  onUnitChange={(unit) => patchPipe({ pipeLengthUnit: unit })}
                />
              </Field>
              <Field id="edit-pipeTolerance" label="Pipe Tolerance">
                <input
                  id="edit-pipeTolerance"
                  className="op-input"
                  placeholder="Optional tolerance"
                  value={pipe.pipeTolerance}
                  onChange={(event) => patchPipe({ pipeTolerance: event.target.value })}
                />
              </Field>
              <Field id="edit-pipeGeometry" label="Pipe Geometry">
                <select
                  id="edit-pipeGeometry"
                  className="op-input"
                  value={pipe.pipeGeometry}
                  onChange={(event) => patchPipe({ pipeGeometry: event.target.value })}
                >
                  <option value="Straight">Straight</option>
                  <option value="Elbow">Elbow</option>
                  <option value="Bend">Bend</option>
                  <option value="Other">Other</option>
                </select>
              </Field>
            </div>

            <h3 className="op-edit-section__title op-edit-section__title--sub">
              Field joint cutbacks
            </h3>
            <p className="op-muted op-cutback-note">
              Supply both long and short cutbacks to close length versions without CAD confirmation.
            </p>
            <div className="op-edit-grid">
              <Field id="edit-cutbackLongSide" label="Cutback — long side">
                <InputWithUnitSelect
                  id="edit-cutbackLongSide"
                  name="cutbackLongSide"
                  value={pipe.cutbackLongSide}
                  unit={pipe.cutbackLongSideUnit}
                  unitOptions={GEOMETRY_UNITS}
                  onValueChange={(value) => patchPipe({ cutbackLongSide: value })}
                  onUnitChange={(unit) => patchPipe({ cutbackLongSideUnit: unit })}
                />
              </Field>
              <Field id="edit-cutbackShortSide" label="Cutback — short side">
                <InputWithUnitSelect
                  id="edit-cutbackShortSide"
                  name="cutbackShortSide"
                  value={pipe.cutbackShortSide}
                  unit={pipe.cutbackShortSideUnit}
                  unitOptions={GEOMETRY_UNITS}
                  onValueChange={(value) => patchPipe({ cutbackShortSide: value })}
                  onUnitChange={(unit) => patchPipe({ cutbackShortSideUnit: unit })}
                />
              </Field>
              <Field id="edit-chamferAngleDeg" label="Chamfer angle">
                <input
                  id="edit-chamferAngleDeg"
                  className="op-input"
                  type="number"
                  min="0"
                  step="0.1"
                  placeholder="Degrees"
                  value={pipe.chamferAngleDeg}
                  onChange={(event) => patchPipe({ chamferAngleDeg: event.target.value })}
                />
              </Field>
              <Field id="edit-ppToeLength" label="PP Toe Length (mm)">
                <input
                  id="edit-ppToeLength"
                  className="op-input"
                  type="text"
                  inputMode="decimal"
                  value={pipe.ppToeLength}
                  onChange={(event) => patchPipe({ ppToeLength: event.target.value })}
                />
              </Field>
              <Field id="edit-ppToeThickness" label="PP Toe Thickness (mm)">
                <input
                  id="edit-ppToeThickness"
                  className="op-input"
                  type="text"
                  inputMode="decimal"
                  value={pipe.ppToeThickness}
                  onChange={(event) => patchPipe({ ppToeThickness: event.target.value })}
                />
              </Field>
              <Field id="edit-overlapOnFbe" label="Overlap on FBE Only (mm)">
                <input
                  id="edit-overlapOnFbe"
                  className="op-input"
                  type="text"
                  inputMode="decimal"
                  value={pipe.overlapOnFbe}
                  onChange={(event) => patchPipe({ overlapOnFbe: event.target.value })}
                />
              </Field>
            </div>
          </section>

          <section className="op-edit-section">
            <h3 className="op-edit-section__title">Process / Coating Specification</h3>
            <div className="op-edit-grid">
              <Field id="edit-coatingType" label="Coating Type">
                <input
                  id="edit-coatingType"
                  className="op-input"
                  placeholder="Enter coating type"
                  value={process.coatingType}
                  onChange={(event) => patchProcess({ coatingType: event.target.value })}
                />
              </Field>
              <Field id="edit-coatingThickness" label="Coating Thickness">
                <InputWithUnitSelect
                  id="edit-coatingThickness"
                  name="coatingThickness"
                  value={process.coatingThickness}
                  unit={process.coatingThicknessUnit}
                  unitOptions={THICKNESS_UNITS}
                  onValueChange={(value) => patchProcess({ coatingThickness: value })}
                  onUnitChange={(unit) => patchProcess({ coatingThicknessUnit: unit })}
                />
              </Field>
              <Field id="edit-heatingRequirement" label="Heating Requirement">
                <input
                  id="edit-heatingRequirement"
                  className="op-input"
                  placeholder="Optional heating requirement"
                  value={process.heatingRequirement}
                  onChange={(event) => patchProcess({ heatingRequirement: event.target.value })}
                />
              </Field>
              <Field id="edit-blastRequirement" label="Blast Requirement">
                <input
                  id="edit-blastRequirement"
                  className="op-input"
                  placeholder="Optional blast requirement"
                  value={process.blastRequirement}
                  onChange={(event) => patchProcess({ blastRequirement: event.target.value })}
                />
              </Field>
              <Field id="edit-surfacePreparation" label="Surface Preparation Requirement">
                <input
                  id="edit-surfacePreparation"
                  className="op-input"
                  placeholder="Optional surface prep requirement"
                  value={process.surfacePreparation}
                  onChange={(event) => patchProcess({ surfacePreparation: event.target.value })}
                />
              </Field>
            </div>
          </section>

          <section className="op-edit-section">
            <h3 className="op-edit-section__title">
              Concrete Weight Coating (leave at 0 if not a CWC pipe)
            </h3>
            <div className="op-edit-grid">
              <Field id="edit-cwcCutbackLongSide" label="CWC Cutback Long Side (mm)">
                <input
                  id="edit-cwcCutbackLongSide"
                  className="op-input"
                  type="text"
                  inputMode="decimal"
                  value={pipe.cwcCutbackLongSide}
                  onChange={(event) => patchPipe({ cwcCutbackLongSide: event.target.value })}
                />
              </Field>
              <Field id="edit-cwcCutbackShortSide" label="CWC Cutback Short Side (mm)">
                <input
                  id="edit-cwcCutbackShortSide"
                  className="op-input"
                  type="text"
                  inputMode="decimal"
                  value={pipe.cwcCutbackShortSide}
                  onChange={(event) => patchPipe({ cwcCutbackShortSide: event.target.value })}
                />
              </Field>
            </div>
          </section>

          {error ? (
            <p className="op-field-error" role="alert">
              {error}
            </p>
          ) : null}
        </div>

        <div className="dialog-footer">
          <CcButton variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </CcButton>
          <CcButton
            variant="primary"
            disabled={saving}
            onClick={() => onSave({ project, pipe, process })}
          >
            {saving ? "Saving…" : "Save & Recalculate"}
          </CcButton>
        </div>
      </div>
    </div>
  );
};

export default EditConfigurationModal;
